import { useLayoutEffect, useRef } from 'react';

/**
 * Observes height changes of the element associated with the returned ref.
 * Calls the onChangeHeight callback when a change occurs.
 */
export default function useHeightObserver({
  onChangeHeight,
}: {
  onChangeHeight: (height: number, target: HTMLElement) => void;
}) {
  const elementRef = useRef<HTMLElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const prevHeightRef = useRef<number>(0);
  const rafRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);

  useLayoutEffect(() => {
    if (!elementRef.current || observerRef.current) {
      return;
    }

    observerRef.current = new ResizeObserver(entries => {
      // Skip processing if we already have a pending update scheduled
      if (rafRef.current !== null) {
        return;
      }

      // Observer callback will always have at least one entry
      const [entry] = entries;
      let height = 0;

      if (entry.contentBoxSize.length > 0) {
        height = entry.contentBoxSize[0]?.blockSize;
      }
      if (height > 0 && height !== prevHeightRef.current) {
        prevHeightRef.current = height;

        // Capture the target element from the observer entry to ensure
        // we're working with the correct element even if the ref changes
        const targetElement = entry.target as HTMLElement;

        // Defer DOM updates to the next animation frame to prevent
        // "ResizeObserver loop completed with undelivered notifications" errors
        rafRef.current = requestAnimationFrame(() => {
          onChangeHeight(height, targetElement);
          rafRef.current = null;
        });
      }
    });
    observerRef.current.observe(elementRef.current);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return elementRef;
}
